import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Outlet, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  IconButton,
  CircularProgress,
  LinearProgress,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MenuIcon from '@mui/icons-material/Menu';
import StraightenIcon from '@mui/icons-material/Straighten';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import GroupIcon from '@mui/icons-material/Group';
import TimelineIcon from '@mui/icons-material/Timeline';
import EventIcon from '@mui/icons-material/Event';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getProject, getPhaseProgress } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import ErrorBoundary from '../../components/ErrorBoundary';

const drawerWidth = 280;
const collapsedWidth = 64;

const PHASE_COLOR = '#F5821F';

const prepareProcesses = [
  {
    id: 'workshop-schedule',
    name: 'Workshop Schedule',
    description: 'Plan and schedule workshops for requirements gathering and design validation',
    icon: <EventIcon />,
    path: 'workshop-schedule'
  },
  {
    id: 'raci-matrix',
    name: 'RACI Matrix',
    description: 'Define roles and responsibilities matrix for project activities and decision making',
    icon: <AccountTreeIcon />,
    path: 'raci-matrix'
  },
  {
    id: 'templates',
    name: 'Project Charter',
    description: 'Download Project Charter with auto-populated project-specific details',
    icon: <DescriptionIcon />,
    path: 'templates'
  }
];

const PreparePhaseLayout = () => {
  const { projectId, processId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [subphaseProgress, setSubphaseProgress] = useState({});
  const [selectedProcess, setSelectedProcess] = useState(null);

  const currentDrawerWidth = collapsed ? collapsedWidth : drawerWidth;

  useEffect(() => {
    const selectedProject = localStorage.getItem('selectedProject');
    if (selectedProject) {
      try {
        const project = JSON.parse(selectedProject);
        const selectedProjectId = project.id ? project.id.toString() : null;
        if (selectedProjectId && projectId !== selectedProjectId) {
          const currentPath = window.location.pathname.split('/').slice(3).join('/');
          navigate(`/projects/${selectedProjectId}/${currentPath}`, { replace: true });
        }
      } catch (e) {
        console.error('Error parsing selected project:', e);
      }
    }
  }, [projectId, navigate]);

  useEffect(() => {
    if (processId) {
      const process = prepareProcesses.find(p => p.path === processId);
      setSelectedProcess(process || null);
    } else {
      setSelectedProcess(null);
    }
  }, [processId]);

  useEffect(() => {
    if (!projectId || projectId === ':projectId') return;
    getPhaseProgress(projectId).then(res => {
      if (res.success && res.subphase_progress) setSubphaseProgress(res.subphase_progress);
    });
  }, [projectId]);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        if (projectId && projectId !== ':projectId') {
          const response = await getProject(projectId);
          if (response.success) {
            setProject(response.project);
          }
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleProcessClick = (process) => {
    navigate(`/projects/${projectId}/prepare/${process.path}`);
    setMobileOpen(false);
  };

  const drawer = (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', p: 1, minHeight: 56 }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
            <AutoAwesomeIcon sx={{ mr: 1, color: PHASE_COLOR }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: PHASE_COLOR }}>
              Prepare Phase
            </Typography>
          </Box>
        )}
        {isMobile ? (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon />
          </IconButton>
        ) : (
          <IconButton onClick={() => setCollapsed(!collapsed)} size="small">
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>
      <Divider />
      <List>
        {prepareProcesses.map((process) => (
          <Tooltip key={process.id} title={collapsed ? process.name : ''} placement="right">
            <ListItem
              button
              selected={selectedProcess?.id === process.id}
              onClick={() => handleProcessClick(process)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(245, 130, 31, 0.12)',
                  borderRight: collapsed ? 0 : 3,
                  borderColor: PHASE_COLOR
                },
                '&.Mui-selected:hover': { backgroundColor: 'rgba(245, 130, 31, 0.20)' },
                '&:hover': { backgroundColor: 'rgba(245, 130, 31, 0.08)' },
                mb: 1,
                mx: collapsed ? 0.5 : 1,
                borderRadius: 1,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 2
              }}
            >
              <ListItemIcon sx={{ color: selectedProcess?.id === process.id ? PHASE_COLOR : 'inherit', minWidth: collapsed ? 0 : 56, justifyContent: 'center' }}>
                {process.icon}
              </ListItemIcon>
              {!collapsed && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ListItemText
                  primary={process.name}
                  secondary={process.description}
                  primaryTypographyProps={{ fontWeight: selectedProcess?.id === process.id ? 600 : 400, color: selectedProcess?.id === process.id ? PHASE_COLOR : 'inherit' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    sx={{ mb: 0.5 }}

                  />
                  <LinearProgress
                    variant="determinate"
                    value={subphaseProgress[process.path] ?? 0}
                    sx={{ height: 3, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.08)' }}
                  />
                </Box>

              )}
            </ListItem>
          </Tooltip>
        ))}
      </List>
    </div>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" color="error">Project not found</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="nav"
        sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 }, zIndex: (theme) => theme.zIndex.appBar - 1, transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }) }}
        aria-label="prepare phase processes"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, mt: '100px' },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              mt: '100px',
              height: 'calc(100vh - 100px)',
              position: 'fixed',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen })
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {isMobile && !mobileOpen && (
        <IconButton
          color="primary"
          aria-label="open drawer"
          onClick={handleDrawerToggle}
          sx={{ position: 'fixed', left: 16, top: 80, zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'background.paper' } }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: 0 },
          mt: 2,
          transition: theme.transitions.create(['width', 'margin'], { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen })
        }}
      >
        <Container maxWidth="xl">
          <ErrorBoundary>
            <Outlet context={{ project, selectedProcess }} />
          </ErrorBoundary>
        </Container>
      </Box>
    </Box>
  );
};

export default PreparePhaseLayout;
